/**
 * Описание задачи хранится в простой разметке (подмножество Markdown):
 *   **жирный**, *курсив*, ~~зачёркнутый~~, [текст](https://ссылка),
 *   строки «> » — цитата, «- » — список, «1. » — нумерованный список.
 * HTML собирается здесь же: весь текст экранируется, ссылки — только http(s) и mailto,
 * поэтому результат безопасно вставлять на страницу и в письмо.
 */

const ESC: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

export function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ESC[c]!);
}

/** Стили для писем: почтовые программы не знают CSS страницы. */
export interface RichTextStyles {
  a?: string;
  blockquote?: string;
  p?: string;
  list?: string;
}

function inline(text: string, st: RichTextStyles): string {
  let s = escapeHtml(text);
  const links: string[] = [];
  // Ссылки — первыми и в «заглушки», чтобы * и ~ внутри адреса не превратились в форматирование.
  s = s.replace(/\[([^\]\n]+)\]\(((?:https?:\/\/|mailto:)[^\s)]+)\)/g, (_, label: string, url: string) => {
    links.push(`<a href="${url}" target="_blank" rel="noopener noreferrer"${st.a ? ` style="${st.a}"` : ''}>${label}</a>`);
    return `\u0000${links.length - 1}\u0000`;
  });
  s = s.replace(/(^|[\s(>«])((?:https?:\/\/)[^\s<]+[^\s<.,;:!?)»])/g, (_, pre: string, url: string) => {
    links.push(`<a href="${url}" target="_blank" rel="noopener noreferrer"${st.a ? ` style="${st.a}"` : ''}>${url}</a>`);
    return `${pre}\u0000${links.length - 1}\u0000`;
  });
  s = s
    .replace(/\*\*(?=\S)([\s\S]*?\S)\*\*/g, '<strong>$1</strong>')
    .replace(/(^|[^*\w])\*(?=\S)([^*\n]*?\S)\*(?![*\w])/g, '$1<em>$2</em>')
    .replace(/~~(?=\S)([\s\S]*?\S)~~/g, '<s>$1</s>');
  return s.replace(/\u0000(\d+)\u0000/g, (_, i: string) => links[Number(i)]!);
}

type Block = { type: 'p' | 'quote' | 'ul' | 'ol'; lines: string[] };

function blocks(md: string): Block[] {
  const out: Block[] = [];
  for (const raw of md.replace(/\r\n?/g, '\n').split('\n')) {
    const line = raw.replace(/\s+$/, '');
    const type: Block['type'] | null = /^>\s?/.test(line) ? 'quote' : /^[-*•]\s+/.test(line) ? 'ul' : /^\d+[.)]\s+/.test(line) ? 'ol' : line ? 'p' : null;
    if (!type) {
      out.push({ type: 'p', lines: [] }); // пустая строка разделяет абзацы
      continue;
    }
    const text = type === 'quote' ? line.replace(/^>\s?/, '') : type === 'ul' ? line.replace(/^[-*•]\s+/, '') : type === 'ol' ? line.replace(/^\d+[.)]\s+/, '') : line;
    const last = out[out.length - 1];
    if (last && last.type === type && (type !== 'p' || last.lines.length > 0)) last.lines.push(text);
    else out.push({ type, lines: [text] });
  }
  return out.filter((b) => b.lines.length > 0);
}

export function richTextToHtml(md: string | null | undefined, st: RichTextStyles = {}): string {
  if (!md) return '';
  const attr = (s?: string) => (s ? ` style="${s}"` : '');
  return blocks(md)
    .map((b) => {
      const lines = b.lines.map((l) => inline(l, st));
      switch (b.type) {
        case 'quote':
          return `<blockquote${attr(st.blockquote)}>${lines.join('<br>')}</blockquote>`;
        case 'ul':
        case 'ol':
          return `<${b.type}${attr(st.list)}>${lines.map((l) => `<li>${l}</li>`).join('')}</${b.type}>`;
        default:
          return `<p${attr(st.p)}>${lines.join('<br>')}</p>`;
      }
    })
    .join('');
}

/** Одна строка с форматированием (заголовок): жирный, курсив, зачёркнутый, ссылки — без абзацев и списков. */
export function richTextInlineToHtml(md: string | null | undefined, st: RichTextStyles = {}): string {
  if (!md) return '';
  return inline(md.replace(/\s*\n\s*/g, ' ').trim(), st);
}

/** Текст без разметки: для писем в текстовом виде и превью. */
export function richTextToPlain(md: string | null | undefined): string {
  if (!md) return '';
  const links: string[] = [];
  return md
    .replace(/\[([^\]\n]+)\]\(((?:https?:\/\/|mailto:)[^\s)]+)\)/g, (_, label: string, url: string) => {
      links.push(`${label} (${url})`);
      return `\u0000${links.length - 1}\u0000`;
    })
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/(^|[^*\w])\*([^*\n]+)\*(?![*\w])/g, '$1$2')
    .replace(/~~(.+?)~~/g, '$1')
    .replace(/^>\s?/gm, '« ')
    .replace(/\u0000(\d+)\u0000/g, (_, i: string) => links[Number(i)]!)
    .trim();
}
